import React from 'react';
import { CalendarClock, Plus, Loader2, MapPin, Link2, Users } from 'lucide-react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import { Label } from '../../../ui/label';
import { Textarea } from '../../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Card, Chip, EmptyState, fmtDateTime, INTERVIEW_STATUS_BADGES, OUTCOME_BADGES } from './drawerShared';

const InterviewForm = ({ form, setForm, saving, onSubmit, onCancel }) => (
  <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/70 p-4" data-testid="interview-form">
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="space-y-1">
        <Label className="text-xs">Date &amp; Time *</Label>
        <Input type="datetime-local" className="h-9 bg-white" value={form.scheduled_at} onChange={(e) => setForm((p) => ({ ...p, scheduled_at: e.target.value }))} data-testid="interview-datetime-input" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Interviewer(s) — comma separated</Label>
        <Input className="h-9 bg-white" value={form.interviewers} placeholder="e.g. John Doe, Mary Lee" onChange={(e) => setForm((p) => ({ ...p, interviewers: e.target.value }))} data-testid="interview-interviewers-input" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Location</Label>
        <Input className="h-9 bg-white" value={form.location} placeholder="Office / Room" onChange={(e) => setForm((p) => ({ ...p, location: e.target.value }))} data-testid="interview-location-input" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs">Meeting Link</Label>
        <Input className="h-9 bg-white" value={form.meeting_link} placeholder="https://..." onChange={(e) => setForm((p) => ({ ...p, meeting_link: e.target.value }))} data-testid="interview-link-input" />
      </div>
    </div>
    <div className="space-y-1">
      <Label className="text-xs">Notes</Label>
      <Textarea rows={2} className="bg-white" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} />
    </div>
    <div className="flex justify-end gap-2">
      <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
      <Button size="sm" onClick={onSubmit} disabled={saving} data-testid="interview-submit-btn">
        {saving && <Loader2 className="mr-1 h-3 w-3 animate-spin" />} Schedule Interview
      </Button>
    </div>
  </div>
);

const InterviewItem = ({ iv, canEdit, onChange }) => (
  <li className="rounded-lg border border-slate-200 bg-white p-3.5" data-testid={`interview-item-${iv.interview_id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <span className="text-sm font-semibold text-slate-900">{fmtDateTime(iv.scheduled_at)}</span>
      <div className="flex items-center gap-1.5">
        <Chip tone={INTERVIEW_STATUS_BADGES[iv.status] || ''}>{(iv.status || '').replace('_', ' ')}</Chip>
        <Chip tone={OUTCOME_BADGES[iv.outcome] || ''}>{iv.outcome}</Chip>
      </div>
    </div>
    <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
      {iv.interviewers?.length > 0 && <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{iv.interviewers.join(', ')}</span>}
      {iv.location && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{iv.location}</span>}
      {iv.meeting_link && <a href={iv.meeting_link} target="_blank" rel="noreferrer" className="inline-flex max-w-[260px] items-center gap-1 truncate text-blue-600 hover:underline"><Link2 className="h-3 w-3 shrink-0" />{iv.meeting_link}</a>}
    </div>
    {iv.notes && <p className="mt-2 rounded bg-slate-50 p-2 text-xs text-slate-600">{iv.notes}</p>}
    {canEdit && (
      <div className="mt-3 flex items-center gap-2">
        <Select value={iv.status} onValueChange={(v) => onChange(iv.interview_id, 'status', v)}>
          <SelectTrigger className="h-7 w-32 text-xs" data-testid={`interview-status-select-${iv.interview_id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="scheduled">Scheduled</SelectItem><SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem><SelectItem value="no_show">No Show</SelectItem>
          </SelectContent>
        </Select>
        <Select value={iv.outcome} onValueChange={(v) => onChange(iv.interview_id, 'outcome', v)}>
          <SelectTrigger className="h-7 w-28 text-xs" data-testid={`interview-outcome-select-${iv.interview_id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Pending</SelectItem><SelectItem value="passed">Passed</SelectItem><SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
      </div>
    )}
  </li>
);

export const InterviewsCard = ({ interviews, canEdit, showForm, setShowForm, form, setForm, saving, onSubmit, onChange }) => {
  const upcoming = interviews.filter((i) => i.status === 'scheduled').length;
  const completed = interviews.filter((i) => i.status === 'completed').length;
  const scheduleBtn = canEdit && (
    <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={() => setShowForm((v) => !v)} data-testid="drawer-schedule-interview-btn">
      <Plus className="h-3 w-3" /> Schedule
    </Button>
  );
  return (
    <Card title={`Interviews (${interviews.length})`} icon={CalendarClock} action={scheduleBtn} data-testid="drawer-interviews-section">
      {interviews.length > 0 && (
        <div className="mb-3 flex gap-2 text-[11px] font-semibold">
          <Chip tone="bg-blue-50 text-blue-700">{upcoming} upcoming</Chip>
          <Chip tone="bg-emerald-50 text-emerald-700">{completed} completed</Chip>
        </div>
      )}
      {showForm && <div className="mb-3"><InterviewForm form={form} setForm={setForm} saving={saving} onSubmit={onSubmit} onCancel={() => setShowForm(false)} /></div>}
      {interviews.length === 0 && !showForm ? (
        <EmptyState icon={CalendarClock} title="No interviews scheduled" hint="Schedule the first interview to start tracking outcomes." testId="interviews-empty" />
      ) : (
        <ul className="space-y-2.5">{interviews.map((iv) => <InterviewItem key={iv.interview_id} iv={iv} canEdit={canEdit} onChange={onChange} />)}</ul>
      )}
    </Card>
  );
};
